import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Award, FileText, ArrowLeft, Send, Loader2, Check, Gift, AlertTriangle, BookOpen } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EssayContestPage = () => {
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [essay, setEssay] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/essay-contest/info`).then(r => setInfo(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    setWordCount(essay.trim() ? essay.trim().split(/\s+/).length : 0);
  }, [essay]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (wordCount < 5000) { setError(`Your essay is ${wordCount} words. Minimum is 5,000 words (target ~8,000).`); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/essay-contest/submit`, { name, email, essay });
      setSubmitted(true);
      toast.success("Essay submitted successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="essay-contest-section">
        <div className="container mx-auto max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          {/* Hero */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFB7B2]/20 to-[#A69BE0]/20 flex items-center justify-center mx-auto mb-5">
              <Award className="h-8 w-8 text-[#FFB7B2]" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A] mb-3" data-testid="essay-title">
              Share Your Journey, Win Your Recovery
            </h1>
            <p className="text-sm text-[#1A2A3A]/60 max-w-lg mx-auto">
              Write your story. Three winners receive the complete Recovery Starter Kit ($37 value) with ALL bonuses for free, plus a $10 discount on any upgrade.
            </p>
          </div>

          {/* Prize Card */}
          <Card className="bg-gradient-to-br from-[#4FB3D4]/5 to-[#DCD6F7]/10 rounded-2xl border-2 border-[#4FB3D4]/20 mb-8" data-testid="prize-card">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Gift className="h-5 w-5 text-[#FFB7B2]" />
                    <h3 className="font-heading text-lg font-semibold text-[#1A2A3A]">What You Win</h3>
                  </div>
                  <ul className="space-y-2">
                    {[
                      "Free Recovery Starter Kit ($37 value)",
                      "15 Ways to Stop a Narcissist PDF",
                      "Recovery Pattern Analysis Workbook",
                      "30-Day Recovery Journal",
                      "AI Recovery Companion (Hope) access",
                      "$10 discount code for Growth ($37 instead of $47)",
                      "$10 discount code for Premium ($57 instead of $67)",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#1A2A3A]/70">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white rounded-xl p-5 border border-[#E5E7EB]/50">
                  <h4 className="font-semibold text-[#1A2A3A] mb-3 text-sm">Contest Details</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-[#1A2A3A]/60"><strong className="text-[#1A2A3A]">Winners:</strong> 3 essays selected</p>
                    <p className="text-[#1A2A3A]/60"><strong className="text-[#1A2A3A]">Length:</strong> ~8,000 words (min 5,000)</p>
                    <p className="text-[#1A2A3A]/60"><strong className="text-[#1A2A3A]">Judge:</strong> Brian Bass (BriVonB)</p>
                    <p className="text-[#1A2A3A]/60"><strong className="text-[#1A2A3A]">Criteria:</strong> Authenticity, impact, potential to help others</p>
                    {info && <p className="text-[#4FB3D4] font-medium">{info.submissions_so_far} essays submitted so far</p>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Writing Guidelines */}
          <Card className="rounded-xl shadow-card border border-[#E5E7EB]/50 mb-8" data-testid="guidelines-card">
            <CardContent className="p-6">
              <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-[#A69BE0]" /> Writing Guidelines
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-[#1A2A3A] mb-2">What to write about:</h4>
                  <ul className="space-y-1.5 text-sm text-[#1A2A3A]/60">
                    <li>- How the relationship started (the beginning)</li>
                    <li>- What happened — the abuse, manipulation, turning points (the middle)</li>
                    <li>- Where you are now — recovery, lessons, hope (the end)</li>
                    <li>- Write in first person (I, me, my)</li>
                    <li>- Be honest. Raw truth beats polished prose</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#1A2A3A] mb-2">Basic rules:</h4>
                  <ul className="space-y-1.5 text-sm text-[#1A2A3A]/60">
                    <li>- Use paragraphs (no giant blocks of text)</li>
                    <li>- Include a clear beginning, middle, and end</li>
                    <li>- You may use a pen name for privacy</li>
                    <li>- Do NOT include real names of abusers (legal reasons)</li>
                    <li>- No hate speech, threats, or promotion of violence</li>
                    <li>- By submitting, you grant ThryvLife permission to publish</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submission Form */}
          <Card className="rounded-2xl shadow-floating border-0" data-testid="essay-form">
            <CardContent className="p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
                    <Check className="h-8 w-8 text-green-500" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-3">Your Essay Has Been Submitted</h3>
                  <p className="text-sm text-[#1A2A3A]/60 max-w-md mx-auto mb-6">
                    Thank you for your courage. Brian will personally review every submission. If you are selected as one of the three winners, we will contact you at the email you provided. Your story could help change someone's life.
                  </p>
                  <Link to="/">
                    <Button className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 font-semibold">Back to Home</Button>
                  </Link>
                </div>
              ) : (
                <>
                  <h3 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-1">Submit Your Essay</h3>
                  <p className="text-xs text-[#1A2A3A]/50 mb-5">Your story is powerful. Share it with us.</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Your Name (or Pen Name) *</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder="Brian Bass" className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="essay-name" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Email (for winner notification) *</label>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@email.com" className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="essay-email" />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-[#1A2A3A]">Your Essay *</label>
                        <span className={`text-xs font-medium ${wordCount >= 8000 ? "text-green-500" : wordCount >= 5000 ? "text-amber-500" : "text-[#1A2A3A]/40"}`} data-testid="word-count">
                          {wordCount.toLocaleString()} / ~8,000 words
                        </span>
                      </div>
                      <textarea value={essay} onChange={e => setEssay(e.target.value)} rows={20} required placeholder="Begin your story here. Take your time. There is no rush. Write from the heart about your journey — the relationship, the demise, the turning point, and where you are today..." className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm resize-none leading-relaxed" data-testid="essay-text" />
                    </div>
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2" data-testid="essay-error">
                        <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}
                    <Button type="submit" disabled={submitting || wordCount < 5000} className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-8 py-5 font-semibold text-sm" data-testid="essay-submit-btn">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      {submitting ? "Submitting..." : "Submit My Essay"}
                    </Button>
                    <p className="text-[10px] text-[#1A2A3A]/40">We do not promise to cure any disease or mental illness. Contest prizes are digital products only.</p>
                  </form>
                </>
              )}
            </CardContent>
          </Card>

          {/* $10 Discount Upsell */}
          <div className="mt-10 text-center">
            <p className="text-sm text-[#1A2A3A]/60 mb-4">Do not want to wait for the contest? Get started now with a $10 discount:</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/checkout?plan=growth">
                <Button variant="outline" className="rounded-full px-6 py-3 text-sm border-2 border-[#A69BE0]/30 hover:bg-[#A69BE0]/5" data-testid="discount-growth">
                  Growth Bundle: <span className="line-through text-[#1A2A3A]/40 mx-1">$47</span> <span className="text-[#A69BE0] font-bold">$37</span> with code THRYV10
                </Button>
              </Link>
              <Link to="/checkout?plan=premium">
                <Button variant="outline" className="rounded-full px-6 py-3 text-sm border-2 border-[#1A2A3A]/20 hover:bg-[#1A2A3A]/5" data-testid="discount-premium">
                  Premium: <span className="line-through text-[#1A2A3A]/40 mx-1">$67</span> <span className="text-[#1A2A3A] font-bold">$57</span> with code THRYV10
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 bg-[#1A2A3A] text-white text-center">
        <p className="text-white/40 text-xs">&copy; 2025 ThryvLife by Brian Bass (BriVonB). Incremental Steps. Exponential Thryving.</p>
      </footer>
    </div>
  );
};

export default EssayContestPage;
